import React, { useEffect, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { Button, Input, Text } from '../../../../components';
import type { TierBlockProps } from './types';
import {
  parseBulkEditDraft,
  patternsToBulkEditDraft,
  shouldSyncDraftFromStrings,
} from './utils';

const EMPTY_TIER_PLACEHOLDER: string[] = [''];

export const TierBlock: React.FC<TierBlockProps> = ({
  tierKey,
  strings,
  disabled,
  bulkEditMode = true,
  onChange,
}) => {
  const [textAreaDraft, setTextAreaDraft] = useState(() =>
    patternsToBulkEditDraft(strings)
  );
  const [isFocused, setIsFocused] = useState(false);
  const wasBulkEditModeRef = useRef(bulkEditMode);

  useEffect(() => {
    const justEnteredBulkEditMode = bulkEditMode && !wasBulkEditModeRef.current;
    wasBulkEditModeRef.current = bulkEditMode;

    const shouldSync = shouldSyncDraftFromStrings({
      bulkEditMode,
      justEnteredBulkEditMode,
      isFocused,
      textAreaDraft,
      strings,
    });

    if (shouldSync) {
      setTextAreaDraft(patternsToBulkEditDraft(strings));
    }
  }, [bulkEditMode, strings, isFocused, textAreaDraft]);

  const handleTextAreaChange = (nextDraft: string) => {
    setTextAreaDraft(nextDraft);
    const parsed = parseBulkEditDraft(nextDraft);
    onChange(parsed.length > 0 ? parsed : EMPTY_TIER_PLACEHOLDER);
  };

  const handleRemoveTier = () => {
    setTextAreaDraft('');
    onChange([]);
  };

  const updateString = (index: number, value: string) => {
    const updated = [...strings];
    updated[index] = value;
    onChange(updated);
  };

  const addString = () => onChange([...strings, '']);

  const removeString = (index: number) => {
    onChange(strings.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col gap-2">
      <Text
        as="span"
        variant="secondary"
        className="text-small font-semibold uppercase tracking-wide"
      >
        {tierKey.replace('_', ' ')}
      </Text>

      {bulkEditMode ? (
        <div className="flex items-stretch gap-2">
          <div className="flex-1">
            <Input
              multiline
              size="small"
              value={textAreaDraft}
              disabled={disabled}
              placeholder={'One pattern per line, e.g.\n\\bplease leave.*message\\b'}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onChange={handleTextAreaChange}
            />
          </div>
          {!disabled && (
            <Button
              type="contrast"
              size="empty"
              tooltip="Remove tier"
              icon={<DeleteIcon fontSize="small" color="error" />}
              onClick={handleRemoveTier}
              className="px-3"
            />
          )}
        </div>
      ) : (
        <>
          {strings.map((str, i) => (
            <div key={i} className="flex items-stretch gap-2">
              <div className="flex-1">
                <Input
                  value={str}
                  disabled={disabled}
                  placeholder="e.g. \bplease leave.*message\b"
                  onChange={val => updateString(i, val)}
                />
              </div>
              {!disabled && (
                <Button
                  type="contrast"
                  size="empty"
                  tooltip="Remove pattern"
                  icon={<DeleteOutlineIcon fontSize="small" color="error" />}
                  onClick={() => removeString(i)}
                  className="px-3"
                />
              )}
            </div>
          ))}

          {!disabled && (
            <Button
              type="outline"
              size="small"
              label="Add pattern"
              icon={<AddIcon />}
              onClick={addString}
              className="bg-white hover:bg-select"
            />
          )}
        </>
      )}
    </div>
  );
};
